// src/screens/SampleOrdersScreen.js - REFACTORED + FIXED (drop-in)
// ✅ NEW STANDARD: sampleOrders identity uses createdBy / createdByName / createdByRole
// ✅ NO requestedBy / requestedById / requestedByUid / requestedByName / requestedByRole
// ✅ No legacy fallbacks tied to requester identity

import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import useSampleOrderCart from "../hooks/useSampleOrderCart";
import {
  PlusSquare,
  ClipboardList,
  LayoutGrid,
  CalendarDays,
  Table2,
  Package,
  BarChart3,
  Lock,
  X,
} from "lucide-react";

// ✅ in-app notifiers (best-effort; screen will NEVER treat notify failures as update failures)
import {
  notifySampleOrderAssigned,
} from "../utils/notifications";

// ✅ NEW: centralized visibility
import { filterSampleOrdersForUser } from "../utils/sampleOrderVisibility";

// Import tab components
import SampleOrderForm from "../components/sampleorders/SampleOrderForm";
import SampleOrderList from "../components/sampleorders/SampleOrderList";
import SampleOrderKanban from "../components/sampleorders/SampleOrderKanban";
import SampleOrderCalendar from "../components/sampleorders/SampleOrderCalendar";
import SampleOrderTable from "../components/sampleorders/SampleOrderTable";
import EditSampleOrderForm from "../components/sampleorders/EditSampleOrderForm";
import EditKit from "../components/sampleorders/EditKit";
import SampleOrdersAnalytics from "../components/metrics/SampleOrdersAnalytics";
import { TaskService } from "../components/tasks/TaskService";
import useDeveloperFeatureFlags from "../hooks/useDeveloperFeatureFlags";
import { getFeatureAccessState, mergeFeatureFlags } from "../utils/featureAccess";

// Custom hooks
import { useAuth } from "../hooks/useAuth";
import { useSampleOrdersData } from "../hooks/useSampleOrdersData";
import { useSampleOrderOperations } from "../hooks/useSampleOrderOperations";
import { useKitOperations } from "../hooks/useKitOperations";
import { useDeepLinking } from "../hooks/useDeepLinking";
import { useCartPrefill } from "../hooks/useCartPrefill";

// Utils
import { normalizeCartItems, itemsKey } from "../utils/cartHelpers";
import { canUserEditKit, getKitScope } from "../utils/sampleKitPermissions";
import SurfaceState from "../components/common/SurfaceState";
import {
  normalizeCompanyType,
  shouldUseIndustrySampleStatusLabels,
} from "../utils/sampleOrderStatusLabels";

// ✅ NO legacy status fields — standard is req.status only
const getReqStatus = (req) => (req?.status ? String(req.status) : null);

const normId = (v) => {
  if (!v) return null;
  if (typeof v === "string") return v;
  if (typeof v === "object") return v.id || v.uid || v.userId || v.value || null;
  return String(v);
};

const getAssignedToId = (req) =>
  normId(req?.assignedToId) || normId(req?.assignedTo) || normId(req?.assignedUserId) || null;

// Initial form state
const INITIAL_FORM_STATE = {
  productId: "",
  quantity: 1,
  requestedDate: "",
  deliveryDate: "",
  accountName: "",
  notes: "",
  assignedToId: "",
  accountId: "",
  items: [],
  kitId: "",
  groupMode: "single",
};

const SampleOrdersScreen = () => {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Auth state
  const {
    firebaseUser,
    userProfile,
    companyId,
    loading: authLoading,
    error: authError,
    myRole,
    isAdmin,
    canDecrementStock,
    myId,
  } = useAuth();

  // Data state
  const {
    inventory,
    sampleOrders,
    companyUsers,
    accounts,
    companySettings,
    kits,
    loading: dataLoading,
    error: dataError,
    loadInventory,
    loadSampleOrders,
    loadAccounts,
    loadKits,
    setSampleOrders, // ✅ needed for optimistic UI updates
  } = useSampleOrdersData(companyId, userProfile);

  // ✅ filtered array for ALL views (end user visibility stays intact)
  const visibleSampleOrders = useMemo(() => {
    return filterSampleOrdersForUser(sampleOrders || [], {
      userId: myId,
      role: myRole,
      groupIds: userProfile?.groupIds || [],
      customerAccountId: userProfile?.accountId || null,
      visibleAccountIds: (accounts || []).map((account) => account?.id).filter(Boolean),
    });
  }, [sampleOrders, myId, myRole, userProfile, accounts]);

  // Local state
  const [activeTab, setActiveTab] = useState("create");
  const [form, setForm] = useState(INITIAL_FORM_STATE);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isCreateSheetOpen, setIsCreateSheetOpen] = useState(false);
  const [error, setError] = useState("");
  const [formResetKey, setFormResetKey] = useState(0);
  const [desktopFormPanelHeight, setDesktopFormPanelHeight] = useState(null);
  const developerFeatureFlags = useDeveloperFeatureFlags();
  const desktopFormPanelRef = useRef(null);

  const safeSetActiveTab = useCallback((tab) => setActiveTab(tab), []);
  const mobileTabs = useMemo(() => ["list"], []);
  const desktopTabs = useMemo(
    () => ["create", "kanban", "calendar", "table", "kits", "metrics"],
    []
  );
  const mobileActiveTab = mobileTabs.includes(activeTab) ? activeTab : "list";
  const desktopActiveTab = desktopTabs.includes(activeTab) ? activeTab : "create";

  const sampleOrderMetricsAccess = useMemo(
    () =>
      getFeatureAccessState({
        featureKey: "sampleOrderMetrics",
        featureFlags: mergeFeatureFlags(companySettings?.featureFlags || {}, developerFeatureFlags || {}),
        userEmail: firebaseUser?.email || userProfile?.email || "",
        planType: companySettings?.planType || userProfile?.planType || null,
        subscriptionActive:
          typeof companySettings?.subscriptionActive === "boolean"
            ? companySettings.subscriptionActive
            : userProfile?.subscriptionActive === true,
        stripeSubscriptionId: companySettings?.stripeSubscriptionId || "",
        seatLimits: companySettings?.seatLimits || null,
      }),
    [
      companySettings?.featureFlags,
      companySettings?.planType,
      companySettings?.subscriptionActive,
      companySettings?.stripeSubscriptionId,
      companySettings?.seatLimits,
      developerFeatureFlags,
      firebaseUser?.email,
      userProfile?.email,
      userProfile?.planType,
      userProfile?.subscriptionActive,
    ]
  );
  const sampleOrderMetricsHiddenEntirely = sampleOrderMetricsAccess.reason === "hidden_entirely";

  useEffect(() => {
    if (!sampleOrderMetricsAccess.allowed && activeTab === "metrics") {
      safeSetActiveTab("create");
    }
  }, [sampleOrderMetricsAccess.allowed, activeTab, safeSetActiveTab]);

  /**
   * ✅ IMPORTANT FIX for bulk edits:
   * We do NOT "skip" updates if one is already in-flight.
   * Instead we queue updates PER requestId so multiple rapid updates
   * to the same doc run in order (prevents races, never drops updates).
   */
  const updateQueueRef = useRef(new Map()); // requestId -> Promise chain

  const enqueueUpdate = useCallback((requestId, task) => {
    const prev = updateQueueRef.current.get(requestId) || Promise.resolve();
    const next = prev
      .catch(() => {}) // prevent chain from breaking if previous failed
      .then(task)
      .finally(() => {
        // cleanup if this is still the latest promise
        const cur = updateQueueRef.current.get(requestId);
        if (cur === next) updateQueueRef.current.delete(requestId);
      });

    updateQueueRef.current.set(requestId, next);
    return next;
  }, []);

  // Shared cart hook
  const { itemsArray: cartItemsArray, replace: cartReplace, clear: cartClear } = useSampleOrderCart({
    companyId,
    currentUserId: myId,
  });

  // Normalized cart keys for sync
  const normalizedFormItems = useMemo(() => normalizeCartItems(form.items), [form.items]);
  const normalizedCartItems = useMemo(() => normalizeCartItems(cartItemsArray), [cartItemsArray]);
  const formItemsKey = useMemo(() => itemsKey(normalizedFormItems), [normalizedFormItems]);
  const cartItemsKey = useMemo(() => itemsKey(normalizedCartItems), [normalizedCartItems]);

  // Sample order operations
  const { saving, createSampleOrder, updateSampleOrder } = useSampleOrderOperations({
    companyId,
    firebaseUser,
    userProfile,
    companySettings,
    inventory,
    accounts,
    companyUsers,
    canDecrementStock,
    onReload: {
      sampleOrders: loadSampleOrders,
      accounts: () => loadAccounts(userProfile),
      inventory: loadInventory,
    },
    onError: setError,
  });

  // Deep linking
  useDeepLinking({
    loading: authLoading || dataLoading,
    sampleOrders: visibleSampleOrders,
    accounts,
    onRequestSelect: (req) => {
      setSelectedRequest(req);
      safeSetActiveTab("create");
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    onAccountSelect: (account) => {
      setForm((prev) => ({
        ...prev,
        accountId: account.id,
        accountName: account.name || prev.accountName || "",
      }));
      safeSetActiveTab("create");
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    onTabChange: (tab) => safeSetActiveTab(tab),
  });

  // Cart prefill
  useCartPrefill({
    loading: authLoading || dataLoading,
    companyId,
    myId,
    selectedRequest,
    cartItemsArray,
    cartItemsKey,
    cartReplace,
    onFormUpdate: (normalizedIncoming) => {
      setForm((prev) => {
        if (Array.isArray(prev.items) && prev.items.length > 0) return prev;
        return { ...prev, productId: "", quantity: 1, items: normalizedIncoming };
      });
      safeSetActiveTab("create");
      setSelectedRequest(null);
    },
  });

  // Two-way sync: form items <-> shared cart
  useEffect(() => {
    if (authLoading || dataLoading) return;
    if (!companyId || !myId) return;
    if (selectedRequest) return;

    if (formItemsKey !== cartItemsKey) {
      cartReplace?.(normalizedFormItems);
    }
  }, [
    authLoading,
    dataLoading,
    companyId,
    myId,
    selectedRequest,
    formItemsKey,
    cartItemsKey,
    cartReplace,
    normalizedFormItems,
  ]);

  useEffect(() => {
    if (!selectedRequest?.id) return;
    const liveMatch = (sampleOrders || []).find((order) => order?.id === selectedRequest.id);
    if (!liveMatch) return;

    setSelectedRequest((prev) => {
      if (!prev || prev.id !== liveMatch.id) return prev;
      return { ...prev, ...liveMatch };
    });
  }, [sampleOrders, selectedRequest?.id]);

  // Handlers
  const handleChange = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmit = useCallback(
    async (e, options) => {
      e.preventDefault();
      const success = await createSampleOrder(form, options, sampleOrders);

      if (success) {
        cartClear?.();
        setForm(INITIAL_FORM_STATE);
        setFormResetKey((prev) => prev + 1);
        setIsCreateSheetOpen(false);
      }
    },
    [createSampleOrder, form, sampleOrders, cartClear]
  );

  /**
   * ✅ DROP-IN FIX: onUpdateRequest used by Table/Kanban/Calendar/Edit
   * - RETURNS a Promise (so the table can await bulk saves and show banner)
   * - Optimistically updates sampleOrders state so UI reacts immediately
   * - Queues per requestId to prevent races & avoid dropped updates
   * - Notifications remain best-effort (do not affect success)
   */
  const handleUpdateRequest = useCallback(
    async (requestId, updates) => {
      if (!companyId || !requestId) return Promise.resolve();

      return enqueueUpdate(requestId, async () => {
        const oldReq = (sampleOrders || []).find((r) => r?.id === requestId) || null;

        const oldStatus = getReqStatus(oldReq);
        const oldAssignedToId = getAssignedToId(oldReq);

        const nextReq = { ...(oldReq || {}), ...(updates || {}) };
        const newStatus = getReqStatus(nextReq);
        const newAssignedToId = getAssignedToId(nextReq);

        const actorName =
          userProfile?.fullName ||
          userProfile?.displayName ||
          firebaseUser?.displayName ||
          firebaseUser?.email ||
          "Someone";

        // ✅ Optimistic UI update FIRST (so all views react instantly)
        // If the save fails, we'll revert by reloading (or you could store a snapshot).
        setSampleOrders((prev) =>
          (prev || []).map((r) => (r?.id === requestId ? { ...r, ...(updates || {}) } : r))
        );

        // Keep selectedRequest synced too (edit panel)
        setSelectedRequest((prev) => {
          if (!prev || prev?.id !== requestId) return prev;
          return { ...prev, ...(updates || {}) };
        });

        try {
          // ✅ MUST be awaited and MUST throw on failure
          await updateSampleOrder(requestId, updates, sampleOrders, isAdmin);
          setError("");
          setSelectedRequest((prev) => (prev?.id === requestId ? null : prev));
        } catch (err) {
          console.error("[SampleOrdersScreen] updateSampleOrder failed:", err);
          setError(err?.message || "Failed to update sample request.");

          // ✅ Revert safely by reloading (keeps logic simple & consistent)
          // (We do NOT swallow error — table banner needs this to reject)
          try {
            await loadSampleOrders();
          } catch (reloadErr) {
            console.warn("[SampleOrdersScreen] reload after failed save also failed:", reloadErr);
          }

          throw err;
        }

        // ✅ Assignment notification (best-effort)
        if (newAssignedToId && newAssignedToId !== oldAssignedToId) {
          try {
            if (newAssignedToId !== companyId) {
              await notifySampleOrderAssigned({
                companyId,
                sampleRequestId: requestId,
                sampleRequest: nextReq,
                assignedUserId: newAssignedToId,
                assignedByName: actorName,
              });
            }
          } catch (e) {
            console.warn("[SampleOrdersScreen] notifySampleOrderAssigned failed (ignored):", e);
          }
        }

        // ✅ Auto-create follow-up task when a request moves into Follow Up
        if (newStatus === "Follow Up" && oldStatus !== "Follow Up") {
          try {
            const existingTasks = await TaskService.getTasksBySampleOrderId(companyId, requestId);
            const existingFollowUp = (existingTasks || []).find((task) => (task.status || "") !== "Cancelled");

            if (!existingFollowUp) {
              const followUpAssigneeId = newAssignedToId || normId(nextReq?.createdBy) || firebaseUser?.uid || null;
              const followUpTitle = `${nextReq.accountName || "Sample order"} follow-up`;
              const followUpProducts = Array.isArray(nextReq.items)
                ? nextReq.items.map((item) => item?.productName || item?.name).filter(Boolean)
                : [];
              const followUpSubtasks = [
                { title: "Confirm sample delivery and customer receipt", isDone: false },
                { title: "Collect feedback on the samples", isDone: false },
                {
                  title: followUpProducts.length
                    ? `Review interest in: ${followUpProducts.slice(0, 2).join(", ")}${followUpProducts.length > 2 ? "..." : ""}`
                    : "Review next-step product interest",
                  isDone: false,
                },
              ];
              const created = await TaskService.createTask(companyId, {
                title: followUpTitle,
                description: nextReq.notes || "Follow up with this sample request.",
                status: "To Do",
                priority: "Medium",
                type: "Follow-up",
                dueDate: nextReq.deliveryDate || nextReq.requestedDate || "",
                dueTime: "09:00",
                assignMode: "users",
                assignedToUserIds: followUpAssigneeId ? [followUpAssigneeId] : [],
                accountId: nextReq.accountId || "",
                sampleOrderId: requestId,
                sampleOrderStatus: newStatus,
                productNames: followUpProducts,
                subtasks: followUpSubtasks,
                createdByUid: firebaseUser?.uid || null,
                createdByRole: userProfile?.role || "endUser",
                updatedByUid: firebaseUser?.uid || null,
                updatedByRole: userProfile?.role || "endUser",
              });

              const followUpTaskId = created?.createdTaskIds?.[0] || null;
              if (followUpTaskId) {
                setSampleOrders((prev) =>
                  (prev || []).map((r) => (r?.id === requestId ? { ...r, followUpTaskId } : r))
                );
                setSelectedRequest((prev) => {
                  if (!prev || prev?.id !== requestId) return prev;
                  return { ...prev, followUpTaskId };
                });

                try {
                  await updateSampleOrder(requestId, { followUpTaskId }, sampleOrders, isAdmin);
                } catch (taskLinkErr) {
                  console.warn("[SampleOrdersScreen] Failed to save followUpTaskId back to request:", taskLinkErr);
                }
              }
            }
          } catch (followUpErr) {
            console.warn("[SampleOrdersScreen] auto follow-up task creation failed:", followUpErr);
          }
        }

        return true;
      });
    },
    [
      companyId,
      enqueueUpdate,
      sampleOrders,
      isAdmin,
      updateSampleOrder,
      loadSampleOrders,
      userProfile,
      firebaseUser,
      setSampleOrders,
    ]
  );

  const handleOpenRequestInEdit = useCallback(
    (order) => {
      setIsCreateSheetOpen(false);
      setSelectedRequest(order);
      if (isMobile) {
        return;
      }
      safeSetActiveTab("create");
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    [isMobile, safeSetActiveTab]
  );

  const handleOpenCreateSheet = useCallback(() => {
    setSelectedRequest(null);
    setIsCreateSheetOpen(true);
    setForm((prev) => ({ ...INITIAL_FORM_STATE, assignedToId: prev.assignedToId || INITIAL_FORM_STATE.assignedToId }));
  }, []);

  const handleCloseCreateSheet = useCallback(() => {
    setIsCreateSheetOpen(false);
    setForm(INITIAL_FORM_STATE);
    setFormResetKey((prev) => prev + 1);
  }, []);

  const handleCloseEditSheet = useCallback(() => {
    setSelectedRequest(null);
  }, []);

  const handleStartDesktopCreate = useCallback(() => {
    setSelectedRequest(null);
    setForm(INITIAL_FORM_STATE);
    setFormResetKey((prev) => prev + 1);
  }, []);

  const desktopListCreateAction = (
    <button
      type="button"
      onClick={handleStartDesktopCreate}
      className="inline-flex h-9 items-center gap-2 rounded-lg bg-blue-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
    >
      <PlusSquare className="h-4 w-4" />
      New Sample Order
    </button>
  );

  const mobileListCreateAction = (
    <button
      type="button"
      onClick={handleOpenCreateSheet}
      className="inline-flex h-9 items-center gap-2 rounded-lg bg-blue-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
    >
      <PlusSquare className="h-4 w-4" />
      New Sample Order
    </button>
  );

  const {
    editingKit,
    savingKit,
    setEditingKit,
    createNewKit,
    openKit,
    saveKit,
    deleteKit,
  } = useKitOperations({
    companyId,
    firebaseUser,
    userProfile,
    onReload: loadKits,
    onError: setError,
  });

  const sortedKits = useMemo(() => {
    return [...(kits || [])].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [kits]);

  const loading = authLoading || dataLoading;
  const combinedError = error || dataError || authError;
  const sampleStatusLabelSettings = useMemo(
    () => ({
      companyType: companySettings?.businessType || companySettings?.companyType || "",
      businessType: companySettings?.businessType || companySettings?.companyType || "",
      useIndustrySampleStatusLabels: shouldUseIndustrySampleStatusLabels(companySettings),
      hasIndustryLabels: !!normalizeCompanyType(
        companySettings?.businessType || companySettings?.companyType || ""
      ),
    }),
    [companySettings]
  );

  useEffect(() => {
    if (desktopActiveTab !== "create") {
      setDesktopFormPanelHeight(null);
      return undefined;
    }

    const node = desktopFormPanelRef.current;
    if (!node) return undefined;

    const updateHeight = () => {
      const nextHeight = Math.ceil(node.getBoundingClientRect().height || 0);
      setDesktopFormPanelHeight(nextHeight > 0 ? nextHeight : null);
    };

    updateHeight();

    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => updateHeight());
    observer.observe(node);
    return () => observer.disconnect();
  }, [desktopActiveTab, selectedRequest, formResetKey, visibleSampleOrders.length]);

  const renderDesktopTabContent = () => {
    switch (desktopActiveTab) {
      case "create":
        return (
          <div className="hidden md:grid md:grid-cols-3 md:gap-4 xl:gap-6">
            <div ref={desktopFormPanelRef} className="md:col-span-2 min-w-0 self-start">
              {selectedRequest ? (
                <EditSampleOrderForm
                  request={selectedRequest}
                  inventory={inventory}
                  companyUsers={companyUsers}
                  accounts={accounts}
                  onSave={handleUpdateRequest}
                  onCancel={() => setSelectedRequest(null)}
                  isAdmin={isAdmin}
                  currentUserId={myId}
                  currentUserGroupIds={userProfile?.groupIds || []}
                  deliveryDays={companySettings?.deliveryDays}
                  sampleRequestLeadDays={companySettings?.sampleRequestLeadDays}
                  companyId={companyId}
                  inline
                  sampleStatusLabelSettings={sampleStatusLabelSettings}
                />
              ) : (
                <SampleOrderForm
                  form={form}
                  inventory={inventory}
                  companyUsers={companyUsers}
                  accounts={accounts}
                  kits={kits}
                  saving={saving}
                  onSubmit={handleSubmit}
                  onChange={handleChange}
                  deliveryDays={companySettings?.deliveryDays}
                  sampleRequestLeadDays={companySettings?.sampleRequestLeadDays}
                  isAdmin={isAdmin}
                  currentUserId={myId}
                  companySettings={companySettings}
                  userProfile={userProfile}
                  resetKey={formResetKey}
                />
              )}
            </div>

            <div className="md:col-span-1 min-w-0">
              <div
                className="flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
                style={desktopFormPanelHeight ? { height: `${desktopFormPanelHeight}px` } : undefined}
              >
                <div className="min-h-0 flex-1 overflow-y-auto p-3">
                  <SampleOrderList
                    requests={visibleSampleOrders}
                    companyUsers={companyUsers}
                    currentUserId={myId}
                    currentUserRole={myRole}
                    currentUserAccountId={userProfile?.accountId || null}
                    currentUserAccountIds={(accounts || []).map((account) => account?.id).filter(Boolean)}
                    onSelectRequest={handleOpenRequestInEdit}
                    headerAction={desktopListCreateAction}
                    sampleStatusLabelSettings={sampleStatusLabelSettings}
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case "kanban":
        return (
          <div className="hidden md:block">
            <SampleOrderKanban
              requests={visibleSampleOrders}
              companyUsers={companyUsers}
              currentUserId={myId}
              currentUserRole={myRole}
              currentUserAccountId={userProfile?.accountId || null}
              onUpdateRequest={handleUpdateRequest}
              onSelectRequest={handleOpenRequestInEdit}
              sampleStatusLabelSettings={sampleStatusLabelSettings}
            />
          </div>
        );

      case "calendar":
        return (
          <div className="hidden md:block">
            <SampleOrderCalendar
              requests={visibleSampleOrders}
              companyUsers={companyUsers}
              currentUserId={myId}
              currentUserRole={myRole}
              currentUserAccountId={userProfile?.accountId || null}
              onUpdateRequest={handleUpdateRequest}
              onSelectRequest={handleOpenRequestInEdit}
            />
          </div>
        );

      case "table":
        return (
          <div className="hidden md:block">
            <SampleOrderTable
              requests={visibleSampleOrders}
              companyUsers={companyUsers}
              isAdmin={isAdmin}
              currentUserId={myId}
              currentUserRole={myRole}
              currentUserGroupIds={userProfile?.groupIds || []}
              currentUserAccountIds={(accounts || []).map((account) => account?.id).filter(Boolean)}
              onUpdateRequest={handleUpdateRequest}
              onSelectRequest={handleOpenRequestInEdit}
              sampleStatusLabelSettings={sampleStatusLabelSettings}
            />
          </div>
        );

      case "kits":
        return (
          <div className="hidden md:block">
            <div className="grid grid-cols-1 xl:grid-cols-5 gap-6 items-start">
              <div className="xl:col-span-2 rounded-2xl border border-slate-200 bg-white shadow-sm p-4">
                <div className="flex items-center justify-between gap-3 mb-4">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">Sample Kits</div>
                    <div className="text-xs text-slate-500 mt-1">
                      Shared defaults for admins, personal kits for individual reps.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={createNewKit}
                    className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                  >
                    New Kit
                  </button>
                </div>

                <div className="space-y-2">
                  {sortedKits.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
                      No kits yet.
                    </div>
                  ) : (
                    sortedKits.map((kit) => {
                      const scope = getKitScope(kit);
                      const canEdit = canUserEditKit(kit, userProfile);
                      const itemCount = Array.isArray(kit.items) ? kit.items.length : 0;
                      return (
                        <button
                          key={kit.id}
                          type="button"
                          onClick={() => openKit(kit)}
                          className={`w-full rounded-2xl border px-4 py-3 text-left transition-colors ${
                            editingKit?.id === kit.id
                              ? "border-blue-300 bg-blue-50"
                              : "border-slate-200 bg-white hover:bg-slate-50"
                          }`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="truncate text-sm font-semibold text-slate-900">
                                {kit.name || "Untitled kit"}
                              </div>
                              <div className="mt-1 text-xs text-slate-500">
                                {itemCount} item{itemCount === 1 ? "" : "s"} •{" "}
                                {scope === "company" ? "Default" : "Personal"}
                              </div>
                            </div>
                            <span
                              className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                                canEdit
                                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                  : "bg-slate-100 text-slate-600 border border-slate-200"
                              }`}
                            >
                              {canEdit ? "Editable" : "View only"}
                            </span>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="xl:col-span-3">
                {editingKit ? (
                  <EditKit
                    kit={editingKit}
                    inventory={inventory}
                    saving={savingKit}
                    canEdit={canUserEditKit(editingKit, userProfile)}
                    onSave={saveKit}
                    onCancel={() => setEditingKit(null)}
                    onDelete={deleteKit}
                  />
                ) : (
                  <div className="flex h-full min-h-[28rem] items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 text-center">
                    <div>
                      <Package className="mx-auto h-10 w-10 text-slate-300" />
                      <div className="mt-3 text-sm font-semibold text-slate-700">Select a kit</div>
                      <div className="mt-1 text-xs text-slate-500">
                        Open an existing kit or create a new one to manage its items.
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case "metrics":
        return sampleOrderMetricsAccess.allowed ? (
          <div className="hidden md:block">
            <SampleOrdersAnalytics
              companyId={companyId}
              sampleOrders={visibleSampleOrders}
              companyUsers={companyUsers}
              accounts={accounts}
              isAdmin={isAdmin}
              currentUserId={myId}
              companySettings={companySettings}
              onSelectRequest={handleOpenRequestInEdit}
            />
          </div>
        ) : null;

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="w-full max-w-lg">
          <SurfaceState
            tone="loading"
            title="Loading sample orders"
            description="Bringing in your requests, inventory, accounts, and team assignments now."
          />
        </div>
      </div>
    );
  }

  if (!firebaseUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center border border-red-100">
          <h2 className="text-xl font-semibold text-slate-900 mb-2">No active session</h2>
          <p className="text-slate-600">
            No user is logged in. Please sign in again to view your sample orders.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-none mx-0 px-0 py-1">
        <div className="bg-white overflow-visible md:rounded-2xl md:shadow-lg md:border md:border-slate-200">
        <div className="hidden md:block border-b border-slate-200">
          <div className="flex items-stretch">
            <nav className="flex overflow-x-auto text-sm px-2">
            <DesktopTabButton
              active={desktopActiveTab === "create"}
              onClick={() => safeSetActiveTab("create")}
              icon={ClipboardList}
              label="Create"
            />
            <DesktopTabButton
              active={desktopActiveTab === "kanban"}
              onClick={() => safeSetActiveTab("kanban")}
              icon={LayoutGrid}
              label="Kanban"
            />
            <DesktopTabButton
              active={desktopActiveTab === "calendar"}
              onClick={() => safeSetActiveTab("calendar")}
              icon={CalendarDays}
              label="Calendar"
            />
            <DesktopTabButton
              active={desktopActiveTab === "table"}
              onClick={() => safeSetActiveTab("table")}
              icon={Table2}
              label="Table"
            />
            <DesktopTabButton
              active={desktopActiveTab === "kits"}
              onClick={() => safeSetActiveTab("kits")}
              icon={Package}
              label="Kits"
            />
            {sampleOrderMetricsHiddenEntirely ? null : (
              <DesktopTabButton
                active={desktopActiveTab === "metrics"}
                onClick={() => sampleOrderMetricsAccess.allowed && safeSetActiveTab("metrics")}
                icon={BarChart3}
                label="Metrics"
                disabled={!sampleOrderMetricsAccess.allowed}
                trailingIcon={!sampleOrderMetricsAccess.allowed ? Lock : null}
                title={!sampleOrderMetricsAccess.allowed ? (sampleOrderMetricsAccess.lockMessage || "Metrics locked") : ""}
              />
            )}
            </nav>
            <div className="ml-auto min-w-[24px]" />
          </div>
        </div>

        <div className="space-y-3 p-2.5 md:p-4 md:space-y-4">
          {combinedError && (
            <SurfaceState
              tone="error"
              compact
              title="Sample orders data issue"
              description={combinedError}
            />
          )}

          {!combinedError &&
          !selectedRequest &&
          accounts.length === 0 &&
          (mobileActiveTab === "create" || desktopActiveTab === "create") ? (
            <SurfaceState
              tone="empty"
              compact
              title="You need an account before requesting samples"
              description="Add or import an account first, then come back here to create your first sample order."
            />
          ) : null}

          {renderDesktopTabContent()}

          {mobileActiveTab === "list" && (
            <div className="overflow-hidden md:hidden">
              <div className="space-y-3">
                <SampleOrderList
                  requests={visibleSampleOrders}
                  companyUsers={companyUsers}
                  currentUserId={myId}
                  currentUserRole={myRole}
                  currentUserAccountId={userProfile?.accountId || null}
                  currentUserAccountIds={(accounts || []).map((account) => account?.id).filter(Boolean)}
                  onSelectRequest={handleOpenRequestInEdit}
                  headerAction={mobileListCreateAction}
                  sampleStatusLabelSettings={sampleStatusLabelSettings}
                />
              </div>
            </div>
          )}

        </div>
      </div>

      {isCreateSheetOpen ? (
        <div className="fixed inset-0 z-[60] flex items-end justify-center p-0 sm:items-center sm:p-4">
          <div className="absolute inset-0 bg-black/60" onClick={handleCloseCreateSheet} />
          <div
            className="relative flex w-full max-w-6xl max-h-[94vh] min-h-0 flex-col overflow-hidden rounded-t-3xl border border-slate-200 bg-white shadow-2xl sm:max-h-[90vh] sm:rounded-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2.5 sm:px-6 sm:py-4">
              <div className="min-w-0">
                <h2 className="truncate text-lg font-semibold text-slate-900">New Sample Order</h2>
                <p className="mt-0.5 truncate text-xs text-slate-500">
                  Build one sample order with multiple line items or use a prebuilt kit.
                </p>
              </div>
              <button
                onClick={handleCloseCreateSheet}
                className="h-9 w-9 rounded-lg hover:bg-slate-100 flex items-center justify-center transition-colors"
                aria-label="Close"
                type="button"
              >
                <X className="w-5 h-5 text-slate-600" />
              </button>
            </div>

            <SampleOrderForm
              form={form}
              inventory={inventory}
              companyUsers={companyUsers}
              accounts={accounts}
              kits={kits}
              saving={saving}
              onSubmit={handleSubmit}
              onChange={handleChange}
              deliveryDays={companySettings?.deliveryDays}
              sampleRequestLeadDays={companySettings?.sampleRequestLeadDays}
              isAdmin={isAdmin}
              currentUserId={myId}
              companySettings={companySettings}
              userProfile={userProfile}
              resetKey={formResetKey}
              embedded
              onCancel={handleCloseCreateSheet}
            />
          </div>
        </div>
      ) : null}

      {selectedRequest && isMobile ? (
        <div className="fixed inset-0 z-[60] flex items-end justify-center p-0 sm:items-center sm:p-4">
          <div className="absolute inset-0 bg-black/60" onClick={handleCloseEditSheet} />
          <div
            className="relative flex w-full max-w-6xl max-h-[94vh] min-h-0 flex-col overflow-hidden rounded-t-3xl border border-slate-200 bg-white shadow-2xl sm:max-h-[90vh] sm:rounded-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2.5 sm:px-6 sm:py-4">
              <div className="min-w-0">
                <h2 className="truncate text-lg font-semibold text-slate-900">Edit Sample Request</h2>
                <p className="mt-0.5 truncate text-xs text-slate-500">
                  {selectedRequest?.productName || "Untitled product"} · {selectedRequest?.accountName || "No account"}
                </p>
              </div>
              <button
                onClick={handleCloseEditSheet}
                className="h-9 w-9 rounded-lg hover:bg-slate-100 flex items-center justify-center transition-colors"
                aria-label="Close"
                type="button"
              >
                <X className="w-5 h-5 text-slate-600" />
              </button>
            </div>
            <EditSampleOrderForm
              request={selectedRequest}
              inventory={inventory}
              companyUsers={companyUsers}
              accounts={accounts}
              onSave={handleUpdateRequest}
              onCancel={handleCloseEditSheet}
              isAdmin={isAdmin}
              currentUserId={myId}
              currentUserGroupIds={userProfile?.groupIds || []}
              deliveryDays={companySettings?.deliveryDays}
              sampleRequestLeadDays={companySettings?.sampleRequestLeadDays}
              companyId={companyId}
              embedded
              sampleStatusLabelSettings={sampleStatusLabelSettings}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

// Helper Components (unchanged)
const DesktopTabButton = ({ active, onClick, icon: Icon, label, disabled = false, trailingIcon: TrailingIcon = null, title = "" }) => (
  <button
    type="button"
    onClick={() => !disabled && onClick()}
    disabled={disabled}
    title={title}
    className={`flex items-center gap-2 px-4 md:px-6 py-4 font-medium transition-all relative ${
      disabled
        ? "text-slate-400 bg-slate-50 cursor-not-allowed"
        : active
        ? "text-blue-600 bg-blue-50"
        : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
    }`}
  >
    <Icon className="w-4 h-4 md:w-5 md:h-5" />
    <span className="text-sm">{label}</span>
    {active && !disabled && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />}
  </button>
);

export default SampleOrdersScreen;
